import {
  Box,
  SvgIcon,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import GreetingHeader from "../../../components/GreetingHeader";
import SectionHeader from "../../../components/SectionHeader";
import SimpleTable from "../../../components/SimpleTable";
import Grid from "@mui/material/Unstable_Grid2"; // Grid version 2
import HighlightBox from "../../../components/HighlightBox";
import { useNavigate } from "react-router-dom";
import * as React from "react";
import { useEffect, useState } from "react";

import ConnectionStatus from "../../../utils/ConnectionStatus";
import { SnackbarProvider, useSnackbar } from "notistack";
import UseOnlineStatus from "../../../utils/UseOnlineStatus";
import {
  getFromLocalStorage,
  } from "../../../utils/localStorageUtils";
import CustomProgressDialog from "../../../components/CustomProgressDialog";
import ShowErrorAlertDialog from "../../../components/ErrorAlertDialog";
import { useAtom } from "jotai";
import {
  isAuthPageAtom,
  showErrorAlertDialog,
} from "../../../config/AppConfig";
import {
  ALERT,
  ERROR,
  ERROR_WHILE_FETCHING_DATA,
  LOADING_PLEASE_WAIT,
  NO_INTERNET_CONNECTION_FOUND,
  YOU_ARE_OFFLINE,
  YOU_ARE_ONLINE,
} from "../../../constants/Strings";
import { initializeEncryption } from "../../../services/AesGcmEncryption";
import DebugLog from "../../../utils/DebugLog";
import {
  LOGIN_ID,
  MESSAGE_KEY,
  SESSION_ID,
  USER_NAME,
} from "../../../constants/LocalStorageKeyValuePairString";
import {
  generateRandomId,
  generateRequestId,
} from "../../../utils/RequestIdGenerator";
import { ApiErrorCode, ApiType } from "../../../services/ApiTags";
import {
  getCountHeader,
  getPayoutSummary,
} from "../../../services/ApiService";
import OnHoldSummaryTable from "../../../components/OnHoldSummaryTable";
import * as CONSTANT from "../../../constants/Constant";
import useAxiosHookV2 from "../../../hooks/UseAxiosV2";
import { AuthContext } from "../../../context/AuthContext";

const FinanceHomePage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const navigate = useNavigate();

  const isNetworkConnectionAvailable = UseOnlineStatus();

  const [, setAuthStatus] = useAtom(isAuthPageAtom);
  const [getDialogStatus, setErrorDialog] = useAtom(showErrorAlertDialog);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [isPayoutSummaryLoading, setPayoutSummaryLoading] = useState(false);
  const [isOnHoldLoading, setOnHoldLoading] = useState(false);

  const [getProgressbarText, setProgressbarText] = useState("");

  const [payoutSummary, setPayoutSummary] = useState([]);
  const [onHoldData, setOnHoldData] = useState([]);
  const [userName, setUserName] = useState([]);

  const [headerDetails, setHeaderDetails] = useState([]);

  // pagination for Payout
  const [payoutPageSize, setPayoutPageSize] = useState(15);
  const [payoutCurrentPage, setPayoutCurrentPage] = useState(0);
  const [payoutTotalNoOfPages, setPayoutTotalNoOfPages] = useState(0);

  const { data, apiCallError, showProgressBar, fetchData } = useAxiosHookV2();
  const [title, setTitle] = useState(ALERT);
  const [content, setContent] = useState("");
  const [, setError] = useState("");
  // user context
  const { authDetails } = React.useContext(AuthContext);

  const [isDealerLogin, setIsDealerLogin] = useState(false);

  useEffect(() => {
    setUserName(getFromLocalStorage(USER_NAME));

    if (authDetails.sessionId !== "") {
      const userRole = authDetails.role;
      if (
        userRole !== null &&
        userRole !== undefined &&
        !userRole.includes("Dealer Owner")
      ) {
        getHeaderDetails();

        // get Payout Summary
        getPayoutSummaryData(payoutCurrentPage, payoutPageSize);

        // get on hold Summary
        getOnHoldData();
        setIsDealerLogin(false);
      } else {
        setIsDealerLogin(true);
      }
    }

    // show no internet available message
    showNoInternetSnackBar();

    navigate(blockNavigation);
  }, [isNetworkConnectionAvailable, enqueueSnackbar, navigate]);

  function showErrorAlert(title, content) {
    try {
      setError();
      setLoading(false);

      setTitle(title);
      setContent(content);
      setErrorDialog(true);
    } catch (error) {
      DebugLog(error);
    }
  }

  // get count header data
  async function getHeaderDetails() {
    try {
      if (!isNetworkConnectionAvailable) {
        throw new Error(NO_INTERNET_CONNECTION_FOUND);
      }

      setProgressbarText(LOADING_PLEASE_WAIT);
      setLoading(true);

      const contentData = {};

      const encryptedContentData = await initializeEncryption(
        contentData,
        getFromLocalStorage(MESSAGE_KEY),
        ApiType.GET_COUNT_HEADER
      );

      const requestData = {
        requestId: generateRequestId(),
        loginId: getFromLocalStorage(LOGIN_ID),
        sessionId: getFromLocalStorage(SESSION_ID),
        loginUserName: getFromLocalStorage(USER_NAME),
        contentData: encryptedContentData,
      };

      getCountHeader(requestData)
        .then((response) => {
          setHeaderDetails(response.data.result);
          setLoading(false);
        })
        .catch((error) => {
          handleError(error);
        });
    } catch (error) {
      handleError(error);
    }
  }

  async function getPayoutSummaryData(pageNumber, pageSize) {
    try {
      if (!isNetworkConnectionAvailable) {
        throw new Error(NO_INTERNET_CONNECTION_FOUND);
      }
      setPayoutSummaryLoading(true);

      const payoutSummaryContentData = {
        pageNumber: pageNumber,
        pageSize: pageSize,
      };

      const encryptedContentData = await initializeEncryption(
        payoutSummaryContentData,
        getFromLocalStorage(MESSAGE_KEY),
        ApiType.GET_PAYOUT_SUMMARY
      );

      const payoutSummaryRequestData = {
        requestId: generateRequestId(),
        loginId: getFromLocalStorage(LOGIN_ID),
        sessionId: getFromLocalStorage(SESSION_ID),
        contentData: encryptedContentData,
      };

      getPayoutSummary(payoutSummaryRequestData)
        .then((response) => {
          const rowsWithIds = response.data.result.content.map((row) => ({
            ...row,
            id: generateRandomId(),
          }));
          setPayoutSummary(rowsWithIds);
          setPayoutTotalNoOfPages(response.data.result.totalPages);
          setPayoutSummaryLoading(false);
        })
        .catch((error) => {
          setPayoutSummaryLoading(false);
          handleError(error);
        });
    } catch (error) {
      handleError(error);
    }
  }

  // Create Request Object for payouts API
  function createRequestObject(pageNumber, pageSize, selectedFilter, filterValue) {
    // Initialize all fields to null
    let requestObject = {
      pageNumber: pageNumber,
      pageSize: pageSize,

      // sortOrder: null,
      // sortProperty: null,
    };
  
    // Set the appropriate field based on filterCriteria
    if (selectedFilter != null && selectedFilter != undefined && selectedFilter != "" && selectedFilter.paramName != "") {
      requestObject[selectedFilter.paramName] = filterValue;
    }

    return requestObject;
  }

  // payouts pagination page change handler
  const handlePayoutsPageChange = (newPage) => {
    setPayoutCurrentPage(newPage);
    getPayoutSummaryData(newPage, payoutPageSize);
  }

  const showNoInternetSnackBar = () => {
    if (isNetworkConnectionAvailable) {
      enqueueSnackbar(YOU_ARE_ONLINE);
    } else {
      enqueueSnackbar(YOU_ARE_OFFLINE, {
        autoHideDuration: 3000,
        variant: "error",
      });
    }
  };

  useEffect(() => {
    if (showProgressBar) {
      setProgressbarText(LOADING_PLEASE_WAIT);
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [showProgressBar]);

  useEffect(() => {
    if (apiCallError) {
      if (
        apiCallError.data != null &&
        apiCallError.data.responseCode != null &&
        apiCallError.data.responseCode != "-1"
      ) {
        const message =
          apiCallError.response != null
            ? apiCallError.response
            : apiCallError.message;
        showErrorAlert(
          apiCallError.message,
          ERROR_WHILE_FETCHING_DATA + JSON.stringify(message)
        );
      } else {
        const message =
          apiCallError.data != null
            ? apiCallError.data.displayErrorMessage
            : apiCallError.message;
        showErrorAlert(
          ERROR,
          ERROR_WHILE_FETCHING_DATA + JSON.stringify(message)
        );
      }
    }
  }, [apiCallError]);

  function getOnHoldData() {
    try {
      if (isNetworkConnectionAvailable) {
        setOnHoldLoading(true);
        const requestObject = {
          // loginUserId: getFromLocalStorage(USER_ID),
        };

        initializeEncryption(
          requestObject,
          getFromLocalStorage(MESSAGE_KEY),
          ApiType.GET_HOLD_PAYOUT_COUNT_SUMMARY
        ).then((encryptedContentData) => {
          fetchData(
            process.env.REACT_APP_DASHBOARD_ON_PAYAOUT_COUNT_SUMMARY_API_URL,
            ApiType.GET_HOLD_PAYOUT_COUNT_SUMMARY,
            ApiType.POST_METHOD,
            encryptedContentData,
            {},
            (data) => {
              if (data !== null) {
                const rowsWithIds = data.result.map((row) => ({
                  ...row,
                  id: generateRandomId(),
                }));

                setOnHoldData(rowsWithIds);
                setOnHoldLoading(false);
              }
            }
          );
        });
      } else {
        showErrorAlert(ALERT, NO_INTERNET_CONNECTION_FOUND);
      }
    } catch (error) {
      const message = error.response != null ? error.response : error.message;
      showErrorAlert(
        error.message,
        ERROR_WHILE_FETCHING_DATA + JSON.stringify(message)
      );
    }
  }

  // handle error code reusable block
  function handleError(error) {
    setLoading(false);
    if (error.message === NO_INTERNET_CONNECTION_FOUND) {
      // Handle no internet connection error
      showErrorAlert(ALERT, NO_INTERNET_CONNECTION_FOUND);
      return;
    }

    const errorCode = error.data?.errorCode;
    var errorMessage =
      error.response != null ? error.displayErrorMessage : "Unknown";
    if (error.data?.displayErrorMessage) {
      errorMessage = error.data.displayErrorMessage;
    }

    const errorActions = {
      [ApiErrorCode.SESSION_EXPIRED]: () => navigate(CONSTANT.LOGIN),
      [ApiErrorCode.SESSION_ID_NOT_FOUND]: () => navigate("/"),
      [ApiErrorCode.UNABLE_TO_PROCESS_ERROR]: () =>
        navigate(CONSTANT.FINANCE_DASHBOARD),
      default: () =>
        showErrorAlert(
          error.message,
          ERROR_WHILE_FETCHING_DATA + JSON.stringify(errorMessage)
        ),
    };

    (errorActions[errorCode] || errorActions.default)();
  }

  function blockNavigation(location, action) {
    // Block navigation if action is "pop", which indicates back/forward button press
    if (action === "pop") {
      // Optionally, you can show a message to the user before blocking navigation
      alert("Back button is disabled.");
      return false;
    }
    return true; // Allow navigation for other actions like "push" or "replace"
  }

  const handlePayoutsRowClicked = (
    pPayoutCycleName,
    pBatchNo,
    pVersionNo,
    pCommissionType,
    pShowApproval,
    pShowCancel,
    pShowHold,
    pShowRelease,
    pShowRequestApproval,
    pShowRequestRelease
  ) => {
    DebugLog("handlePayoutsRowClicked executed");
    navigate(CONSTANT.PAYOUT_ARCHIVE_ROUTE, {
      state: {
        batchNo: pBatchNo,
        version: pVersionNo,
        payoutCycleName: pPayoutCycleName,
        commissionType: pCommissionType,
        showApproval: pShowApproval,
        showCancel: pShowCancel,
        showHold: pShowHold,
        showRelease: pShowRelease,
        showRequestApproval: pShowRequestApproval,
        showRequestRelease: pShowRequestRelease,

        flowFrom: CONSTANT.FINANCE_DASHBOARD,
      },
    });
  };

  const handleOnHoldRowClicked = (
    pPayoutCycleName,
    pBatchNo,
    pCommissionType,
    pShowApproval,
    pShowCancel,
    pShowHold,
    pShowRelease,
    pShowRequestApproval,
    pShowRequestRelease
  ) => {
    DebugLog("handlePayoutsRowClicked executed");
    navigate(
      // CONSTANT.ON_HOLD_PAYOUT_CYCLE_ROUTE,
      CONSTANT.PAYOUT_DEALER_SUMMARY_ROUTE,
      {
        state: {
          batchNo: pBatchNo,
          holdType: pPayoutCycleName,
          commissionType: pCommissionType,
          flowFrom: CONSTANT.FINANCE_DASHBOARD,
          flowStartedFrom: CONSTANT.FINANCE_DASHBOARD,
          rootStartPageSelectedColumn: "dealerCount",
        },
      }
    );
  };

  return (
    /* Main Container */

    <Box>
      <SnackbarProvider maxSnack={3}>
        <ConnectionStatus />
        <ShowErrorAlertDialog
          status={getDialogStatus}
          title={title}
          content={content}
        />
        {isNetworkConnectionAvailable ? (
          <CustomProgressDialog open={loading} text={getProgressbarText} />
        ) : (
          showNoInternetSnackBar()
        )}
        <Grid
          container
          component="main"
          direction={"column"}
          sx={{
            // height: "100vh",
            m: "0 2.5%" /* Approx 30px */,
            borderRadius: "18px",
          }}
        >
          {/* Greetings Header */}
          <Grid container>
            <Grid item>
              <GreetingHeader
                greeting={"Welcome Back"}
                name={userName + " 👋🏻"}
              ></GreetingHeader>
            </Grid>
          </Grid>
          {/* Greetings Header */}

          {/* Highlights Section */}
          <Box sx={{ whiteSpace: "pre" }}>
            <Grid container spacing={3}>
              {headerDetails[0] && (
                <HighlightBox
                  highlightName={headerDetails[0]?.displayLabel}
                  highlightCount={headerDetails[0]?.count}
                  highlightBG={colors.primary[100]}
                  highlightColor={colors.greenAccent[100]}
                  highlightIcon={
                    <SvgIcon sx={{ width: "24px", height: "auto" }}>
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.4259 17.7066H16.5459V27.3066C16.5459 28.72 18.3059 29.3866 19.2392 28.32L29.3326 16.8533C30.2126 15.8533 29.5059 14.2933 28.1726 14.2933H24.0526V4.69329C24.0526 3.27996 22.2926 2.61329 21.3592 3.67996L11.2659 15.1466C10.3993 16.1466 11.1059 17.7066 12.4259 17.7066Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M11.3333 5.33398H2"
                          stroke="white"
                          strokeWidth="2"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10 26.666H2"
                          stroke="white"
                          strokeWidth="2"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M6 16H2"
                          stroke="white"
                          strokeWidth="2"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </SvgIcon>
                  }
                ></HighlightBox>
              )}

              {headerDetails[1] && (
                <HighlightBox
                  highlightName={headerDetails[1]?.displayLabel}
                  highlightCount={headerDetails[1]?.count}
                  highlightBG={colors.primary[200]}
                  highlightColor={colors.greenAccent[100]}
                  highlightIcon={
                    <SvgIcon sx={{ width: "24px", height: "auto" }}>
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M29.3327 15.9993C29.3327 23.3593 23.3593 29.3327 15.9993 29.3327C8.63935 29.3327 2.66602 23.3593 2.66602 15.9993C2.66602 8.63935 8.63935 2.66602 15.9993 2.66602C23.3593 2.66602 29.3327 8.63935 29.3327 15.9993Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M20.9478 20.2403L16.8145 17.7737C16.0945 17.347 15.5078 16.3203 15.5078 15.4803V10.0137"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </SvgIcon>
                  }
                ></HighlightBox>
              )}

              {/* Validation not included for PHASE 1 */}
              {/* <HighlightBox
                highlightName={headerDetails[2]?.displayLabel}
                highlightCount={headerDetails[2]?.count}
                highlightBG={colors.primary[300]}
                highlightColor={colors.greenAccent[100]}
                highlightIcon={"../../assets/common/Validations.svg"}
              ></HighlightBox> */}
            </Grid>
          </Box>
          {/* Highlights Section */}

          {/* Payouts Section */}
          <Grid
            container
            mt={3}
            border={"1px solid" + colors.grey[600]}
            borderRadius={2}
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
          >
            <SectionHeader
              // sectionIcon={"../../assets/common/Payouts.svg"}
              sectionIcon={
                // <SvgIcon>
                <svg
                  width="36"
                  height="37"
                  viewBox="0 0 36 37"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M32.9993 18.6953C32.9993 26.9753 26.2793 33.6953 17.9993 33.6953C9.71927 33.6953 2.99927 26.9753 2.99927 18.6953C2.99927 10.4153 9.71927 3.69531 17.9993 3.69531C26.2793 3.69531 32.9993 10.4153 32.9993 18.6953Z"
                    stroke="black"
                    stroke-width="2.25"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M23.5663 23.4659L18.9163 20.6909C18.1063 20.2109 17.4463 19.0559 17.4463 18.1109V11.9609"
                    stroke="black"
                    stroke-width="2.25"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                // </SvgIcon>
              }
              sectionHeading={
                isDealerLogin
                  ? "Payouts - Dealer not allowed to see any data here!"
                  : "Payouts"
              }
            ></SectionHeader>

            <SimpleTable
              loading={isPayoutSummaryLoading}
              statusData={"Needs Approval"}
              data={payoutSummary}
              onRowClicked={handlePayoutsRowClicked}
              totalNoOfPages={payoutTotalNoOfPages}
              onPageChange={handlePayoutsPageChange}
            ></SimpleTable>
          </Grid>
          {/* Payouts Section */}

          {/* On Hold Section */}
          <Grid
            container
            mt={3}
            border={"1px solid" + colors.grey[600]}
            borderRadius={2}
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
          >
            <SectionHeader
              // sectionIcon={"../../assets/common/OnHold.svg"}
              sectionIcon={
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 36 36"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.975 28.665V7.335C15.975 5.31 15.12 4.5 12.96 4.5H7.515C5.355 4.5 4.5 5.31 4.5 7.335V28.665C4.5 30.69 5.355 31.5 7.515 31.5H12.96C15.12 31.5 15.975 30.69 15.975 28.665Z"
                    stroke="#292D32"
                    stroke-width="2.25"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M31.4987 28.665V7.335C31.4987 5.31 30.6437 4.5 28.4837 4.5H23.0387C20.8937 4.5 20.0237 5.31 20.0237 7.335V28.665C20.0237 30.69 20.8787 31.5 23.0387 31.5H28.4837C30.6437 31.5 31.4987 30.69 31.4987 28.665Z"
                    stroke="#292D32"
                    stroke-width="2.25"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              }
              sectionHeading={
                isDealerLogin
                  ? "On Hold - Dealer not allowed to see any data here!"
                  : "On Hold"
              }
            ></SectionHeader>

            <OnHoldSummaryTable
              loading={isOnHoldLoading}
              statusData={"On Hold"}
              statusBG={colors.primary[300]}
              data={onHoldData}
              onRowClicked={handleOnHoldRowClicked}
            ></OnHoldSummaryTable>
          </Grid>
          {/* On Hold Section */}

          {/* NOT IN SCOPE OF PHASE 1: Validations Section */}
          {/* <Grid
            container
            mt={3}
            border={"1px solid" + colors.grey[600]}
            borderRadius={2}
            xs={12}
            sm={12}
            md={12}
            lg={12}
            xl={12}
          >
            <SectionHeader
              sectionIcon={"../../assets/common/Validations-black.svg"}
              sectionHeading={"Validation"}
            ></SectionHeader>

            <SimpleTable
              statusData={"In Validation"}
              statusBG={colors.primary[300]}
              //data={payoutSummary}
            ></SimpleTable>
          </Grid> */}
          {/* Validations Section */}

          {/* Add an empty space as padding to the bottom */}
          <Box mt={3}></Box>
        </Grid>
      </SnackbarProvider>
    </Box>
    /* Main Container */
  );
};

export default FinanceHomePage;
